// ============================================================================
// COLLECTION ANIMATION — Animated resource pickup arc to inventory bar
// ============================================================================
import * as THREE from 'three';
import { RESOURCE_DEFS } from '../systems/resources.js';
import { eventBus, GameEvents } from '../systems/events.js';

let camera = null;
let container = null;

// ---------------------------------------------------------------------------
// Public init
// ---------------------------------------------------------------------------

/**
 * Initialise the collection-arc overlay.  Call once after the Three.js camera
 * is ready and the DOM is available.
 *
 * @param {THREE.Camera} cameraRef — the game's perspective camera
 */
export function initCollectionAnimation(cameraRef) {
  camera = cameraRef;

  container = document.createElement('div');
  container.id = 'collection-anim-container';
  container.style.cssText = `
    position: fixed; top: 0; left: 0;
    width: 100%; height: 100%;
    pointer-events: none;
    z-index: 860;
    overflow: hidden;
  `;
  document.body.appendChild(container);

  eventBus.on(GameEvents.RESOURCE_COLLECTED, (data) => {
    if (data.position) {
      playCollectionArc(data.resourceType, data.position);
    }
  });

  console.log('[CollectionAnimation] Initialized');
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Find the on-screen centre of the inventory slot for a given resource type.
 * Falls back to the centre-bottom of the viewport if the slot is not found.
 */
function getInventorySlotCenter(resourceType) {
  const slot = document.querySelector(
    `.inventory-slot[data-resource-type="${resourceType}"]`,
  );
  if (slot) {
    const rect = slot.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }
  // Fallback: centre of inventory bar, or bottom-centre of screen
  const bar = document.getElementById('inventory-bar');
  if (bar) {
    const rect = bar.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }
  return { x: window.innerWidth / 2, y: window.innerHeight - 40 };
}

/**
 * Flash / pulse the inventory slot to draw the player's eye.
 */
function pulseInventorySlot(resourceType) {
  const slot = document.querySelector(
    `.inventory-slot[data-resource-type="${resourceType}"]`,
  );
  if (!slot) return;

  // Use a quick CSS animation for the flash
  slot.style.transition = 'transform 0.12s ease-out, filter 0.12s ease-out';
  slot.style.transform = 'scale(1.35)';
  slot.style.filter = 'brightness(1.6) drop-shadow(0 0 6px gold)';

  setTimeout(() => {
    slot.style.transform = 'scale(1)';
    slot.style.filter = '';
    // Clean up inline styles after transition
    setTimeout(() => {
      slot.style.transition = '';
      slot.style.transform = '';
      slot.style.filter = '';
    }, 150);
  }, 120);
}

// ---------------------------------------------------------------------------
// Core animation
// ---------------------------------------------------------------------------

function playCollectionArc(resourceType, worldPos) {
  const def = RESOURCE_DEFS[resourceType];
  if (!def || !camera) return;

  // --- Project 3D world position to screen coordinates ---
  const pos3D = new THREE.Vector3(worldPos.x, worldPos.y, worldPos.z);
  const screenPos = pos3D.project(camera);

  // Behind camera — skip
  if (screenPos.z > 1) return;

  const startX = (screenPos.x * 0.5 + 0.5) * window.innerWidth;
  const startY = (-screenPos.y * 0.5 + 0.5) * window.innerHeight;

  // Target: the matching inventory slot (or fallback)
  const target = getInventorySlotCenter(resourceType);
  const endX = target.x;
  const endY = target.y;

  // --- Create floating emoji element ---
  const el = document.createElement('div');
  el.textContent = def.icon;
  el.style.cssText = `
    position: absolute;
    font-size: 28px;
    left: ${startX}px;
    top: ${startY}px;
    transform: translate(-50%, -50%) scale(0);
    pointer-events: none;
    will-change: transform, left, top, opacity;
  `;
  container.appendChild(el);

  // --- Timing ---
  const popDuration = 100; // ms — quick scale pop
  const arcDuration = 500; // ms — parabolic arc to inventory
  const totalDuration = popDuration + arcDuration;
  const startTime = performance.now();

  // Arc height scales with the distance so short arcs don't overshoot
  const dx = endX - startX;
  const dy = endY - startY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const arcHeight = Math.min(200, dist * 0.4);

  function animate(now) {
    const elapsed = now - startTime;

    if (elapsed < popDuration) {
      // Phase 1: Scale pop 0 -> 1.3
      const t = elapsed / popDuration;
      const scale = t * 1.3;
      el.style.transform = `translate(-50%, -50%) scale(${scale})`;
    } else if (elapsed < totalDuration) {
      // Phase 2: Parabolic arc + shrink
      const t = (elapsed - popDuration) / arcDuration;
      const easeT = t * t * (3 - 2 * t); // smoothstep

      const x = startX + (endX - startX) * easeT;
      // Parabolic arc peaking at t = 0.5 (negative = upward)
      const parabolaY = -arcHeight * 4 * t * (1 - t);
      const y = startY + (endY - startY) * easeT + parabolaY;

      // Shrink from 1.3 down to ~0
      const scale = 1.3 * (1 - t);
      const opacity = 1 - t * 0.3;

      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.transform = `translate(-50%, -50%) scale(${scale})`;
      el.style.opacity = `${opacity}`;
    } else {
      // Done — remove element and flash the inventory slot
      el.remove();
      pulseInventorySlot(resourceType);
      return;
    }

    requestAnimationFrame(animate);
  }

  requestAnimationFrame(animate);
}
